import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import { logout as logoutAction } from '../../actions/auth';
import * as McpApi from '../../api/mcp';
import { BRAND_NAME } from '../../logic/brand';
import ConfirmDialog from '../../components/Shared/ConfirmDialog';
import ConnectedAppsPage from '../../components/Profile/connected-apps';
import { splitAroundClientAndDomain } from '../../components/Authorize/translations';

const statusOf = (error) => error && error.response && error.response.status;

// The client name and domain come from the client: each is its own text node,
// never part of the interpolated string, and keeps its case.
function RevokeMessage({ t, grant }) {
  const parts = splitAroundClientAndDomain(t, 'mcp.connectedApps.revokeConfirm');

  return (
    <span style={{ textTransform: 'none', overflowWrap: 'anywhere' }}>
      {parts.map((pieces, index) => (
        <React.Fragment key={index}>
          {index > 0 && grant.client.name}
          {pieces.map((piece, pieceIndex) => (
            <React.Fragment key={pieceIndex}>
              {pieceIndex > 0 && grant.client.domain}
              {piece}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </span>
  );
}

export default function ConnectedAppsContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [status, setStatus] = React.useState('loading');
  const [grants, setGrants] = React.useState([]);
  const [revokingId, setRevokingId] = React.useState(null);
  const [attempt, setAttempt] = React.useState(0);
  const [focusTarget, setFocusTarget] = React.useState(null);
  const mounted = React.useRef(true);
  const grantsRef = React.useRef(grants);
  const titleRef = React.useRef(null);
  const revokeButtons = React.useRef(new Map());

  grantsRef.current = grants;

  React.useEffect(() => () => {
    mounted.current = false;
  }, []);

  // A 401 means the session is gone: logging out locally makes PrivateRoute
  // send the user to /login, which brings them back here afterwards.
  const expireSession = React.useCallback(() => dispatch(logoutAction(true)), [dispatch]);

  React.useEffect(() => {
    let active = true;

    setStatus('loading');
    McpApi.listGrants()
      .then(({ data }) => {
        if (!active) {
          return;
        }

        if (Array.isArray(data)) {
          setGrants(data);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        const code = statusOf(error);

        if (code === 401) {
          expireSession();
        } else if (code === 404) {
          // MCP turned off: nothing is connected, which is not an error.
          setGrants([]);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      });

    return () => {
      active = false;
    };
  }, [attempt, expireSession]);

  // Keyboard focus never falls to <body>: after a revoke it goes to the next
  // app's button, or to the title when no app follows.
  React.useEffect(() => {
    if (!focusTarget) {
      return;
    }

    const button = focusTarget.grantId !== undefined && revokeButtons.current.get(focusTarget.grantId);
    const target = button || titleRef.current;

    if (target) {
      target.focus();
    }

    setFocusTarget(null);
  }, [focusTarget, grants, revokingId]);

  const buttonRefFor = (grantId) => (element) => {
    if (element) {
      revokeButtons.current.set(grantId, element);
    } else {
      revokeButtons.current.delete(grantId);
    }
  };

  const removeGrant = (grant) => {
    const current = grantsRef.current;
    const index = current.findIndex((item) => item.id === grant.id);
    const next = current[index + 1];

    setGrants(current.filter((item) => item.id !== grant.id));
    setRevokingId(null);
    setFocusTarget(next ? { grantId: next.id } : {});
    toast.success(t('mcp.connectedApps.revoked'));
  };

  const revoke = (grant) => {
    setRevokingId(grant.id);

    McpApi.revokeGrant(grant.id)
      .then(() => {
        if (mounted.current) {
          removeGrant(grant);
        }
      })
      .catch((error) => {
        if (!mounted.current) {
          return;
        }

        const code = statusOf(error);

        if (code === 401) {
          expireSession();
        } else if (code === 404) {
          // Already revoked (e.g. in another tab): the app is gone either way.
          removeGrant(grant);
        } else {
          setRevokingId(null);
          setFocusTarget({ grantId: grant.id });
          toast.error(t('mcp.connectedApps.revokeError'));
        }
      });
  };

  const confirmRevoke = (grant) => {
    confirmAlert({
      customUI: (props) => (
        <ConfirmDialog {...props} message={<RevokeMessage t={t} grant={grant} />} onConfirmClick={() => revoke(grant)} />
      ),
      // Yes, No and Escape: back to this app's Revoke button (not
      // document.activeElement: Safari does not focus a clicked button).
      afterClose: () => {
        const target = revokeButtons.current.get(grant.id) || titleRef.current;

        if (target) {
          target.focus();
        }
      },
    });
  };

  return (
    <ConnectedAppsPage
      status={status}
      grants={grants}
      brandName={BRAND_NAME}
      revokingId={revokingId}
      onRetry={() => setAttempt((value) => value + 1)}
      onRevoke={confirmRevoke}
      titleRef={titleRef}
      buttonRefFor={buttonRefFor}
    />
  );
}
